/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {loadAccounts} from '@ion/core/src/services/Connect';
import {Account, EntityStatus} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

export interface UseAccountsSearchParams {
  accountId: number;
  pageId: number;
  pageSize: number;
  searchTerms: string;
  shouldSearchById?: boolean;
};

export type UseAccountsSearchResult = {
  account: Account[];
  loadAccounts: (params: UseAccountsSearchParams, callbacks) => void;
};

/**
 * The useAccountsSearch hook is used to retrieve accounts.
 *
 * ```typescript
 *
 *  import {useAccountsSearch} from '@ion/packages/query/src/hooks';
 *
 *  const {accounts} = useAccountsSearch({
 *    accountId,
 *    pageId,
 *    pgSize,
 *    searchTerms,
 *    shouldSearchById
 *  });
 *
 * ```
 */
export const useAccountsSearch = (): UseAccountsSearchResult => {
  const mutation = useMutation(async(params: UseAccountsSearchParams) => {
    const {
      accountId,
      pageId,
      pageSize,
      searchTerms,
      shouldSearchById
    } = params;
    const {accounts: items} = await loadAccounts({
      pageId   : pageId,
      pageSize : pageSize,
      sortOn   : ['+name'],
      ...(!shouldSearchById && {
        parentAccountId  : accountId,
        includeSubGroups : true
      }),
      ...(shouldSearchById && {includeGlobalSearch: true}),
      filterOn : searchTerms?.length ? shouldSearchById ? {
        id : searchTerms
      } : {
        name   : `*${searchTerms?.toLowerCase()}*`,
        status : EntityStatus.Enabled
      } : null
    });
    return items;
  });

  return createMutationResult(
    mutation, {
      loadAccounts : mutation.mutate
    }
  ) as UseAccountsSearchResult;
};

export default useAccountsSearch;
